import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

import { loadSetting, saveSetting } from '../shared/storage/settingsStorage';

import { colors as colorsLight, colorsDark } from './tokens';

/** Widened color type that accepts both light and dark palettes. */
type ThemeColors = {
  [K in keyof typeof colorsLight]: (typeof colorsLight)[K] | (typeof colorsDark)[K];
};

interface ThemeContextValue {
  colors: ThemeColors;
  isDark: boolean;
  toggleDarkMode: () => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  colors: colorsLight,
  isDark: false,
  toggleDarkMode: () => {},
});

/**
 * ThemeProvider — wraps the app and provides reactive dark-mode state.
 *
 * Reads the persisted `darkModeEnabled` setting on mount and exposes
 * `colors` (light or dark palette), `isDark`, and `toggleDarkMode`.
 */
export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDark, setIsDark] = useState(false);

  // Hydrate from persisted settings on mount
  useEffect(() => {
    let mounted = true;
    loadSetting('darkModeEnabled', false).then((value) => {
      if (mounted && typeof value === 'boolean') {
        setIsDark(value);
      }
    });
    return () => { mounted = false; };
  }, []);

  const toggleDarkMode = useCallback(() => {
    setIsDark((prev) => {
      const next = !prev;
      saveSetting('darkModeEnabled', next);
      return next;
    });
  }, []);

  const themeColors = isDark ? colorsDark : colorsLight;

  return (
    <ThemeContext.Provider value={{ colors: themeColors, isDark, toggleDarkMode }}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Hook to consume the app-wide theme from any component.
 *
 * Usage:
 * ```ts
 * const { colors, isDark, toggleDarkMode } = useAppTheme();
 * ```
 */
export function useAppTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}
